import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiService } from '@/services/api/apiService';
import { toast } from 'react-hot-toast';

export const RegisterPage = () => {
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('invite');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [inviteData, setInviteData] = useState(null);
    const [validatingInvite, setValidatingInvite] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const { t } = useLanguage();

    // Validate invitation token on mount
    useEffect(() => {
        if (inviteToken) {
            validateInvitation();
        }
    }, [inviteToken]);

    const validateInvitation = async () => {
        setValidatingInvite(true);
        try {
            const response = await fetch(`/api/invitations/validate/${inviteToken}`);
            const data = await response.json();

            if (response.ok && data.valid) {
                // Auto-fill form with invitation data
                setEmail(data.email);
                setFirstName(data.firstName);
                setLastName(data.lastName);
                // Use the unique username assigned by the backend
                if (data.username) {
                    setUsername(data.username);
                } else {
                    // Fallback (legacy invitations without username)
                    setUsername(data.email.split('@')[0]);
                }
                setInviteData(data);
                toast.success(t('register.invite_valid', '¡Invitación válida! Completa tu registro'));
            } else {
                setError(data.error || t('register.invite_invalid', 'Invitación inválida o expirada'));
                toast.error(t('register.invite_invalid', 'Invitación inválida o expirada'));
            }
        } catch (err) {
            console.error('Error validating invitation:', err);
            setError(t('register.invite_error', 'Error al validar la invitación'));
        } finally {
            setValidatingInvite(false);
        }
    };

    // Validate password strength
    const validatePassword = (pwd) => {
        const errors = [];
        if (pwd.length < 8) {
            errors.push(t('register.password_min_length', 'Mínimo 8 caracteres'));
        }
        if (!/[A-Z]/.test(pwd)) {
            errors.push(t('register.password_uppercase', 'Al menos una mayúscula'));
        }
        if (!/[a-z]/.test(pwd)) {
            errors.push(t('register.password_lowercase', 'Al menos una minúscula'));
        }
        if (!/[0-9]/.test(pwd)) {
            errors.push(t('register.password_number', 'Al menos un número'));
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
            errors.push(t('register.password_special', 'Al menos un símbolo (!@#$...)'));
        }
        return errors;
    };

    // Handle password change with validation
    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (newPassword) {
            setPasswordErrors(validatePassword(newPassword));
        } else {
            setPasswordErrors([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        // Validate password strength
        const pwdErrors = validatePassword(password);
        if (pwdErrors.length > 0) {
            setError(t('register.password_weak', 'La contraseña no cumple los requisitos de seguridad'));
            setPasswordErrors(pwdErrors);
            setIsLoading(false);
            return;
        }

        // Validate password match
        if (password !== confirmPassword) {
            setError(t('register.password_mismatch', 'Las contraseñas no coinciden'));
            setIsLoading(false);
            return;
        }

        try {
            const registerData = {
                email,
                password,
                username,
                firstName,
                lastName
            };

            // If using invitation, include tenant assignment
            if (inviteData) {
                registerData.tenantId = inviteData.tenantId;
                registerData.role = 'student'; // Auto-assign student role
            }

            await apiService.request('/auth/register', {
                method: 'POST',
                body: JSON.stringify(registerData),
            });

            // If invitation was used, mark it as used
            if (inviteToken) {
                try {
                    await fetch(`/api/invitations/mark-used/${inviteToken}`, {
                        method: 'POST'
                    });
                } catch (err) {
                    console.error('Error marking invitation as used:', err);
                    // Don't fail registration if this fails
                }
            }

            toast.success(t('register.success', '¡Registro exitoso! Inicia sesión para continuar'));
            navigate('/login');
        } catch (err) {
            setError(err.message || t('auth.register_error', 'Error al registrar usuario'));
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading state while validating invitation
    if (inviteToken && validatingInvite) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <LandingNavbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-600 font-medium">{t('register.validating', 'Validando invitación...')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <LandingNavbar />
            <div className="flex-1 flex flex-col lg:flex-row">
                {/* Left Column - Info (Hidden on mobile) */}
                <div className="hidden lg:flex lg:w-1/2 bg-[#192A42] text-white p-12 xl:p-24 flex-col justify-center relative overflow-hidden">
                    <div className="relative z-10 space-y-8">
                        <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight">
                            {t('auth.join', 'Únete a')} <br />
                            <span className="text-blue-400">{t('auth.brand_name', 'TrueDok')}</span>
                        </h2>
                        <p className="text-slate-300 text-lg leading-relaxed max-w-lg">
                            {t('auth.register_desc', 'La plataforma líder en certificación digital. Segura, transparente y eficiente.')}
                        </p>
                    </div>
                </div>

                {/* Right Column - Form */}
                <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-12 lg:w-1/2 bg-white">
                    <div className="w-full max-w-md space-y-8">
                        <div className="text-center lg:text-left space-y-2">
                            {inviteData ? (
                                <>
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg mb-4">
                                        <i className="fas fa-check-circle"></i>
                                        <span className="font-semibold">{t('register.invite_badge', 'Invitación válida')}</span>
                                    </div>
                                    <h1 className="text-3xl font-extrabold text-slate-900">
                                        {t('register.welcome', '¡Bienvenido, {name}!').replace('{name}', firstName)}
                                    </h1>
                                    <p className="text-slate-500">
                                        {t('register.complete', 'Completa tu registro para acceder a la plataforma')}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h1 className="text-3xl font-extrabold text-slate-900">{t('auth.create_account', 'Crear Cuenta')}</h1>
                                    <p className="text-slate-500">{t('auth.register_subtitle', 'Regístrate para comenzar a emitir o recibir certificados')}</p>
                                </>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 ml-1">{t('common.first_name', 'Nombre')}</label>
                                    <input
                                        type="text"
                                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none disabled:opacity-60"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        placeholder="Juan"
                                        disabled={!!inviteData}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 ml-1">{t('common.last_name', 'Apellido')}</label>
                                    <input
                                        type="text"
                                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none disabled:opacity-60"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        placeholder="Pérez"
                                        disabled={!!inviteData}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">{t('common.email', 'Email')}</label>
                                <input
                                    type="email"
                                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none disabled:opacity-60"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="email@ejemplo.com"
                                    disabled={!!inviteData}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">{t('common.username', 'Usuario')}</label>
                                <input
                                    type="text"
                                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="juanp123"
                                    required
                                />
                                {inviteData && (
                                    <p className="text-xs text-slate-500 ml-1">
                                        <i className="fas fa-info-circle mr-1"></i>
                                        {t('register.username_hint', 'Puedes cambiar el nombre de usuario sugerido')}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">{t('common.password', 'Contraseña')}</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className="w-full px-5 py-4 pr-12 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder="••••••••"
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                                        aria-label={showPassword ? t('common.hide_password', 'Ocultar contraseña') : t('common.show_password', 'Mostrar contraseña')}
                                    >
                                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-lg`}></i>
                                    </button>
                                </div>
                                {password && passwordErrors.length > 0 && (
                                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <p className="text-xs font-semibold text-amber-800 mb-1">
                                            {t('register.password_requirements', 'La contraseña debe contener:')}
                                        </p>
                                        <ul className="space-y-1">
                                            {passwordErrors.map((err, idx) => (
                                                <li key={idx} className="text-xs text-amber-700 flex items-center gap-1">
                                                    <i className="fas fa-exclamation-circle"></i>
                                                    {err}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {password && passwordErrors.length === 0 && (
                                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                                        <i className="fas fa-check-circle text-green-600"></i>
                                        <span className="text-xs font-semibold text-green-700">
                                            {t('register.password_strong', 'Contraseña segura ✓')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">{t('register.confirm_password', 'Confirmar Contraseña')}</label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        className="w-full px-5 py-4 pr-12 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 transition-all outline-none"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                                        aria-label={showConfirmPassword ? t('common.hide_password', 'Ocultar contraseña') : t('common.show_password', 'Mostrar contraseña')}
                                    >
                                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-lg`}></i>
                                    </button>
                                </div>
                                {confirmPassword && password !== confirmPassword && (
                                    <p className="text-xs text-red-600 ml-1 flex items-center gap-1">
                                        <i className="fas fa-times-circle"></i>
                                        {t('register.password_mismatch', 'Las contraseñas no coinciden')}
                                    </p>
                                )}
                                {confirmPassword && password === confirmPassword && (
                                    <p className="text-xs text-green-600 ml-1 flex items-center gap-1">
                                        <i className="fas fa-check-circle"></i>
                                        {t('register.password_match', 'Las contraseñas coinciden')}
                                    </p>
                                )}
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm font-medium flex items-center gap-2">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg shadow-lg transition-all disabled:opacity-50"
                            >
                                {isLoading ? t('common.registering', 'Registrando...') : t('auth.register_btn', 'Registrarse')}
                            </button>

                            <p className="text-center text-slate-500 mt-6">
                                {t('auth.already_account', '¿Ya tienes cuenta?')} {' '}
                                <Link to="/login" className="text-blue-600 font-bold hover:underline">
                                    {t('nav.login', 'Inicia Sesión')}
                                </Link>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
